import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Alert, TextField, IconButton, Divider, List, ListItem,
  ListItemText, ListItemSecondaryAction, Paper, InputAdornment, Chip, Autocomplete
} from '@mui/material';
import DeleteIcon    from '@mui/icons-material/Delete';
import AddIcon       from '@mui/icons-material/Add';
import CloseIcon     from '@mui/icons-material/Close';
import PersonIcon    from '@mui/icons-material/Person';
import CalculateIcon from '@mui/icons-material/Calculate';
import Api from './Api';
import { useAppTheme } from '../../contexts/ThemeContext';

const EditTransactionModal = ({ open, onClose, transactionData, onConfirm }) => {
  const { palette, Colors } = useAppTheme();
  const [loading,        setLoading]        = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [error,          setError]          = useState(null);
  const [tenants,        setTenants]        = useState([]);
  const [allocations,    setAllocations]    = useState([]);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [totalTransaction, setTotalTransaction] = useState(0);
  const [selectedTenant,   setSelectedTenant]   = useState(null);
  const [allocationAmount, setAllocationAmount] = useState('');

  useEffect(() => {
    if (open && transactionData) {
      setAllocations([]); setSelectedTenant(null); setAllocationAmount('');
      setRemainingAmount(transactionData.amount); setTotalTransaction(transactionData.amount);
      fetchTenants();
    }
  }, [open, transactionData]);

  const fetchTenants = async () => {
    setLoadingTenants(true);
    try { setTenants(await Api.getTenantSelection()); }
    catch (err) { setError(`Error fetching tenants: ${err.message}`); }
    finally { setLoadingTenants(false); }
  };

  const handleAddAllocation = () => {
    if (!selectedTenant || !allocationAmount || parseFloat(allocationAmount) <= 0) { setError('Please select a tenant and enter a valid amount'); return; }
    const amount = parseFloat(allocationAmount);
    if (amount > remainingAmount) { setError(`Amount exceeds remaining balance of KES ${remainingAmount.toFixed(2)}`); return; }
    const idx = allocations.findIndex(a => a.tenant.id === selectedTenant.id);
    let newAllocations = [...allocations];
    if (idx >= 0) newAllocations[idx] = { ...newAllocations[idx], amount: newAllocations[idx].amount + amount };
    else newAllocations.push({ tenant: selectedTenant, amount });
    setAllocations(newAllocations);
    setRemainingAmount(prev => parseFloat((prev - amount).toFixed(2)));
    setSelectedTenant(null); setAllocationAmount(''); setError(null);
  };

  const handleRemoveAllocation = (index) => {
    const a = allocations[index];
    setRemainingAmount(prev => parseFloat((prev + a.amount).toFixed(2)));
    setAllocations(prev => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    if (allocations.length === 0) { setError('Please add at least one allocation'); return; }
    setLoading(true);
    try {
      const result = await Api.distributeTransaction(transactionData.transaction_id,
        allocations.map(a => ({ tenant_id: a.tenant.id, amount: a.amount })));
      onConfirm(result); onClose();
    } catch (err) { setError(`Failed to confirm payment: ${err.message}`); }
    finally { setLoading(false); }
  };

  const sectionBg = { backgroundColor: palette.background.tertiary, border: `1px solid ${palette.border}` };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { backgroundColor: palette.background.secondary, color: palette.text.primary } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Colors.primary.dark}, ${Colors.primary.main})`,
        color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Typography variant="h6">Edit Payment Allocation</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Transaction details */}
        <Box sx={{ ...sectionBg, p: 2, borderRadius: 1, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: palette.text.primary }}>Transaction Details</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[['M-Pesa Ref', transactionData?.transaction_id], ['Amount', `KES ${transactionData?.amount?.toFixed(2)}`],
              ['Phone', transactionData?.phone_number], ['Date', transactionData?.transaction_time]].map(([label, val]) => (
              <Box key={label}>
                <Typography variant="body2" sx={{ color: palette.text.secondary }}>{label}:</Typography>
                <Typography variant="body1" sx={{ color: palette.text.primary }}>{val}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {/* Summary */}
          <Paper elevation={0} sx={{ flex: 1, p: 2, ...sectionBg }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: palette.text.primary }}>Payment Distribution</Typography>
            {[['Total Amount', `KES ${totalTransaction.toFixed(2)}`, false],
              ['Allocated',    `KES ${(totalTransaction - remainingAmount).toFixed(2)}`, false],
              ['Remaining',    `KES ${remainingAmount.toFixed(2)}`, true]].map(([label, val, bold]) => (
              <React.Fragment key={label}>
                {bold && <Divider sx={{ mb: 2, borderColor: palette.border }} />}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: bold ? 0 : 2 }}>
                  <Typography variant="body1" fontWeight={bold ? 'bold' : 'normal'} sx={{ color: palette.text.primary }}>{label}:</Typography>
                  <Typography variant="body1" fontWeight={bold ? 'bold' : 'normal'}
                    sx={{ color: bold ? (remainingAmount === 0 ? Colors.status.success : Colors.status.warning) : palette.text.primary }}>
                    {val}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </Paper>

          {/* Add allocation form */}
          <Paper elevation={0} sx={{ flex: 2, p: 2, ...sectionBg }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: palette.text.primary }}>Add Allocation</Typography>
            {loadingTenants ? (
              <Box display="flex" justifyContent="center" p={2}><CircularProgress size={30} sx={{ color: Colors.primary.main }} /></Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete options={tenants || []} getOptionLabel={o => `${o.name} (${o.unit})`}
                  renderOption={(props, o) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1" sx={{ color: palette.text.primary }}>{o.name}</Typography>
                        <Typography variant="caption" sx={{ color: palette.text.secondary }}>{o.property}, Unit {o.unit}</Typography>
                      </Box>
                    </li>
                  )}
                  value={selectedTenant} onChange={(_, v) => setSelectedTenant(v)}
                  renderInput={params => <TextField {...params} label="Select Tenant" size="small" fullWidth />}
                  sx={{ flex: 2 }}
                />
                <TextField label="Amount" value={allocationAmount} onChange={e => setAllocationAmount(e.target.value)}
                  type="number" size="small" sx={{ flex: 1.5, minWidth: 160 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => { if (!selectedTenant) { setError('Please select a tenant first'); return; } setAllocationAmount(remainingAmount.toString()); }} title="Use full remaining amount">
                          <CalculateIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Button variant="contained" onClick={handleAddAllocation} startIcon={<AddIcon />}
                  disabled={!selectedTenant || !allocationAmount || parseFloat(allocationAmount) <= 0}
                  sx={{ backgroundColor: Colors.primary.main, '&:hover': { backgroundColor: Colors.primary.dark } }}>
                  Add
                </Button>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Allocations list */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: palette.text.primary }}>Payment Allocations</Typography>
        <Paper elevation={0} sx={{ mb: 2, maxHeight: 200, overflow: 'auto', ...sectionBg }}>
          {allocations.length > 0 ? (
            <List>
              {allocations.map((a, i) => (
                <React.Fragment key={`${a.tenant.id}-${i}`}>
                  {i > 0 && <Divider sx={{ borderColor: palette.border }} />}
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      <PersonIcon sx={{ color: Colors.primary.main, fontSize: 'small' }} />
                    </Box>
                    <ListItemText
                      primary={<Typography sx={{ color: palette.text.primary }}>{a.tenant.name}</Typography>}
                      secondary={<Typography variant="body2" sx={{ color: palette.text.secondary }}>{a.tenant.property} — Unit {a.tenant.unit}</Typography>}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      <Chip label={`KES ${a.amount.toFixed(2)}`} variant="outlined" sx={{ borderColor: Colors.primary.main, color: Colors.primary.main }} />
                    </Box>
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleRemoveAllocation(i)}>
                        <DeleteIcon sx={{ color: Colors.status.error }} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: palette.text.muted }}>No allocations added yet. Add tenants to distribute the payment.</Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: palette.text.secondary }}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || allocations.length === 0}
          startIcon={loading && <CircularProgress size={18} color="inherit" />}
          sx={{ backgroundColor: Colors.primary.main, '&:hover': { backgroundColor: Colors.primary.dark } }}>
          {loading ? 'Processing...' : 'Confirm Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTransactionModal;